import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Inject, inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDialogTitle, MatDialogActions, MatDialogContent, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { Store } from '@ngxs/store';
import { firstValueFrom, from, Observable, of } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { CdkDragDrop, CdkDropList, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatIcon } from '@angular/material/icon';
import { getStatusColor, getStatusText, StateFilterCBComponent } from '../../utils/component/state-filter-combobox/state-cb.component';
import { Approval } from '../../core/types';
import { AsyncPipe, NgClass } from '@angular/common';
import { SequenceBlock, SequenceList } from '../interface/sequence-interface';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { ConsumerEntityService, ConsumerEntityState } from '../../consumer/services/consumer.entity.service';
import { SubjectEntityService } from '../../subject/services/subject.entity.service';
import { SubjectEntity } from '../../subject/subject.entity';
import { SequenceEntity } from '../sequence.entity';
import { normalizeManyRelations, normalizeOneRelation } from '../../core/decorators/relation.decorator';
import { ExamEntity } from '../../lesson/exam/exam.entity';
import { ExamEntityService } from '../../lesson/exam/services/exam.entity.service';
import { EntityStateModel } from '../../core/state/entity.state';
import { MultiComboEntitySearchComponent } from '../../utils/component/multi-combo-entity-search/multi-combo-entity-search.component';
import { ExerciseEntity } from '../../lesson/exercise/exercise.entity';
import { ExerciseEntityService } from '../../lesson/exercise/services/exercise.entity.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { ObjectConsumerMetadataComponent } from '../../consumer/object-consumer-metadata/object-consumer-metadata.component';

// union type to use exercises and exams
type TaskEntity = ExamEntity | ExerciseEntity;

@Component({
  selector: 'pixl-sequence-edit',
  imports: [
    MatDialogTitle,
    FormsModule,
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    MatOption,
    MatSelect,
    MatDividerModule,
    MultiComboEntitySearchComponent,
    CdkDropList,
    DragDropModule,
    MatIcon,
    NgClass,
    StateFilterCBComponent,
    AsyncPipe,
    TranslocoModule,
    MatTabGroup,
    MatTab,
    ObjectConsumerMetadataComponent
  ],
  templateUrl: './sequence-edit.component.html',
  styleUrl: './sequence-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SequenceEditComponent implements OnInit {
  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;
  sequences$!: Observable<SequenceEntity[]>;
  exams$!: Observable<ExamEntity[]>;
  exercises$!: Observable<ExerciseEntity[]>;
  selectedConsumerName$!: Observable<string>;

  allSelectedTasks: TaskEntity[] = [];
  splitSelectedTasks: TaskEntity[][] = [];
  currentPage = 1;
  pageSize = 20;

  tasksSearchQuery: string | null = null;

  private store = inject(Store);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private examService = inject(ExamEntityService);
  private exerciseService = inject(ExerciseEntityService);
  private destroyRef = inject(DestroyRef);
  private taskCache = new Map<string, TaskEntity>();

  private cdr = inject(ChangeDetectorRef);
  public searchResultObservables: Record<number, Observable<TaskEntity[]>> = {};

  private thisTasks: SequenceBlock[] = [];

  public dataSequceneList: SequenceBlock[][] = [];

  public formGroup: FormGroup;

  get sequenceList(): SequenceBlock[][] {
    return (this.formGroup.get('sequence')?.value as SequenceList)?.list || [];
  }

  get isExamKind(): boolean {
    return this.data?.kind === 'exam';
  }

  get isExerciseKind(): boolean {
    return this.data?.kind === 'exercise';
  }

  get taskService() {
    return this.isExamKind ? this.examService : this.exerciseService;
  }

  get taskStateClass() {
    return this.isExamKind ?
      this.examService.stateClass :
      this.exerciseService.stateClass;
  }

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<SequenceEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data?: Partial<SequenceEntity>
  ) {
    const initConsumerData: Record<string, unknown> = {};
    const cd = data?.consumerData || {};
    for (const key of Object.keys(cd)) {
      initConsumerData[key] = [cd[key]];
    }

    this.formGroup = this.fb.group({
      name: [data?.name, Validators.required],
      consumerId: data?.consumerId || null,
      subjectId: data?.subjectId || null,
      approval: data?.approval || '',
      sequence: fb.control<SequenceList>(
        data?.sequence || { list: [] }
      ),
      kind: data?.kind,
      consumerData: fb.group(initConsumerData),
      copyRestricted: data?.copyRestricted || null,
      active: data?.active || false,
      changedBy: data?.changedBy || null,
      version: data?.version || 1,
      sortName: data?.sortName || data?.name?.toLowerCase() || null,
      consumers: [normalizeOneRelation<ConsumerEntity>(data?.consumers) || data?.consumerId || null],
      subjects: [normalizeOneRelation<SubjectEntity>(data?.subjects)],
      exams: [normalizeManyRelations(data?.exams)],
      exercises: [normalizeManyRelations(data?.exercises)],
    });

    // clean out empty sequence blocks in the middle
    const rawList = this.data?.sequence?.list;
    if (Array.isArray(rawList)) {
      const cleaned = rawList.filter(block =>
        Array.isArray(block) && block.length > 0
      );
      if (cleaned.length === 0) cleaned.push([]);

      this.formGroup.patchValue({
        sequence: { list: cleaned }
      });
    }
  }

  ngOnInit() {
    this.loadRelationData();

    this.initSearchObservables();

    this.selectedConsumerName$ = this.formGroup.get('consumers')!.valueChanges.pipe(
      startWith(this.formGroup.get('consumers')!.value),
      switchMap(val => {
        const id = Array.isArray(val) ? val[0] : val;
        if (!id) return of('Konsument');
        return this.store.select(ConsumerEntityState.byId).pipe(
          map(fn => fn(id)),
          map(consumer => consumer?.name || 'Konsument')
        );
      })
    );
  }

  private loadRelationData(): void {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery))
      )
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(
      this.subjectService.ensureLoaded(subjectQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery))
      )
    );
  }

  private async initSearchObservables() {
    const relationField = this.isExamKind ? this.data?.exams : this.data?.exercises;
    const idsToLoad = normalizeManyRelations(relationField) as string[];

    const promises = [];

    if (idsToLoad.length > 0) {
      const idQuery = { 'filter[id]': idsToLoad.join(',') };
      promises.push(this.taskService.ensureLoaded(idQuery));
    }

    const pageQuery = {
      'page[size]': this.pageSize,
      'page[number]': this.currentPage,
      'sort': 'name'
    };
    promises.push(this.taskService.ensureLoaded(pageQuery));

    await Promise.all(promises);

    this.getAllSelectedTasksFromState();
    this.getSplitSelectedTasksFromState();

    const sequenceValue = this.formGroup.get('sequence')?.value as SequenceList | undefined;
    const currentSequenceList = sequenceValue?.list || [];
    const baseSearch$ = this.store.select(this.taskService.allByQuery(pageQuery)).pipe(
      map(paginated => {
        const combined: TaskEntity[] = [...this.allSelectedTasks];

        const existingIds = new Set(this.allSelectedTasks.map(t => t.id));

        paginated.forEach(p => {
          if (!existingIds.has(p.id)) {
            combined.push(p);
            existingIds.add(p.id);
          }
        });

        return combined;
      }),
      shareReplay(1)
    );

    for (let i = 0; i < currentSequenceList.length; i++) {
      this.searchResultObservables[i] = baseSearch$;
    }

    const taskIds = this.allSelectedTasks.map(t => t.id);
    if (this.isExamKind) {
      this.formGroup.get('exams')?.setValue(taskIds);
    }
    else {
      this.formGroup.get('exercises')?.setValue(taskIds);
    }

    this.cdr.markForCheck();
  }

  private getAllSelectedTasksFromState(): void {
    const relationField = this.isExamKind ? this.data?.exams : this.data?.exercises;
    const allSelectedTaskIds = normalizeManyRelations(relationField) as string[];

    // Resolve full entities from store
    const state = this.store.selectSnapshot(this.taskStateClass) as EntityStateModel<TaskEntity>;
    this.allSelectedTasks = allSelectedTaskIds
      .map(id => state.entities[id])
      .filter((t): t is TaskEntity => !!t);

    this.rebuildTaskCache();
  }

  private getSplitSelectedTasksFromState(): void {
    const listValue = this.formGroup.get('sequence')?.value as SequenceList | undefined;
    const currentList = listValue?.list || [];

    const splitSelectedTaskIds = currentList.map(row => row.map(item => item.id));

    // Resolve full entities from store
    const state = this.store.selectSnapshot(this.taskStateClass) as EntityStateModel<TaskEntity>;
    this.splitSelectedTasks = splitSelectedTaskIds
      .map(row => row
        .map(id => state.entities[id])
        .filter((e): e is TaskEntity => !!e)
      );
  }

  parseSequenceListData(): void {
    const sequenceValue = this.formGroup.get('sequence')?.value as SequenceList | undefined;
    if (!sequenceValue?.list) return;

    this.dataSequceneList = [];

    sequenceValue.list.forEach((entry: SequenceBlock[], index: number) => {
      this.dataSequceneList[index] = entry.map((task: SequenceBlock) => ({
        id: task.id,
        name: this.getTaskName(task.id),
        type: task.type,
        kind: task.kind,
        approval: this.getTaskApproval(task.id),
        subKind: task.subKind
      }));
    });
  }

  parseEntityToBlock(entity: TaskEntity): SequenceBlock {
    return {
      id: entity?.id || '',
      // kind and type mixed up in the data. This is either exam or exercise
      type: this.data?.kind || '',
      // this kind is one of > 20 different kinds for the task itself
      kind: entity.kind || '',
      subKind: ''
    };
  }

  public onSave(): void {
    if (this.formGroup.invalid) return;

    const formValue = this.formGroup.value;
    const sequenceList = formValue.sequence?.list as SequenceBlock[][] || [];

    // clean out empty sequence blocks before saving
    const cleanedList = sequenceList.filter(block =>
      Array.isArray(block) && block.length > 0
    );

    const cleanedSequence: SequenceList = {
      list: cleanedList.length > 0 ? cleanedList : [[]]
    };

    const updatedSequence = Object.assign(
      new SequenceEntity(),
      { id: this.data?.id },
      formValue,
      {
        sequence: cleanedSequence,
        consumers: formValue.consumers ? { id: formValue.consumers } : [],
        subjects: formValue.subjects ? { id: formValue.subjects } : [],
        exams: (formValue.exams as string[])?.length ?
          (formValue.exams as string[]).map(id => ({ id })) :
          [],
        exercises: (formValue.exercises as string[])?.length ?
          (formValue.exercises as string[]).map(id => ({ id })) :
          [],
      }
    );

    this.dialogRef.close(updatedSequence);
  }

  public checkForErrors(formControl: AbstractControl): string {
    if (formControl.hasError('required')) {
      return 'Eingabe erforderlich';
    }

    return '';
  }

  receiveStateChange(event: TaskEntity[] | null, blockIndex: number): void {
    if (!event) return;

    const currentOptions: TaskEntity[] = [];
    this.searchResultObservables[blockIndex]?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(values =>
      values.forEach(value =>
        currentOptions.push(value)
      )
    );

    const normalizedSelection: TaskEntity[] = event.map(newTask => {
      const existing = currentOptions.find(opt => opt.id === newTask.id);
      if (existing) return existing;
      /**
      * At this place there was a line that also put the newly selected exam into the observable
      * used by the dropdown so all currently seleced exams would be shown at the top.
      * This however is currently not possible with it beeing an observable and no longer a
      * BahaviourSubject. So newly selected Exams wont be shown, the old ones will. Its the
      * best that can be done by now as selectiong tasks for a sequence is going to change soon either way.
      */
      return newTask;
    });
    const blockList: SequenceBlock[] = normalizedSelection.map(value =>
      this.parseEntityToBlock(value)
    );

    const sequenceControl = this.formGroup.get('sequence') as FormControl<SequenceList>;
    const currentSequence = sequenceControl.value;

    if (currentSequence?.list) {
      const newList = [...currentSequence.list];
      newList[blockIndex] = blockList;

      sequenceControl.setValue(
        { list: newList },
        { emitEvent: false }
      );
    }

    const existingIds = new Set(this.thisTasks.map(e => e.id));
    const parsedNormalizedSelection: SequenceBlock[] = [];

    normalizedSelection.forEach(task => {
      if (!existingIds.has(task.id)) {
        this.thisTasks.push(this.parseEntityToBlock(task));
        this.allSelectedTasks = [...this.allSelectedTasks, task];
      }
      parsedNormalizedSelection.push(this.parseEntityToBlock(task));
    });

    this.dataSequceneList[blockIndex] = [...parsedNormalizedSelection];
    this.rebuildTaskCache();
    this.cdr.markForCheck();
  }

  private rebuildTaskCache(): void {
    this.allSelectedTasks.forEach(task => {
      this.taskCache.set(task.id, task);
    });
  }

  async receiveSearchQuery(query: string, blockIndex: number) {
    this.tasksSearchQuery = query?.trim() || null;

    if (this.tasksSearchQuery && this.tasksSearchQuery.length >= 3) {
      const searchParams = { 'filter[name]': this.tasksSearchQuery };
      await this.taskService.ensureLoaded(searchParams);

      this.searchResultObservables[blockIndex] = this.store.select(this.taskService.allByQuery(searchParams));
    }
    else {
      // default paginated mode
      const sequencePageQuery = { 'page[size]': this.pageSize, 'page[number]': this.currentPage, 'sort': 'name' };
      await this.taskService.ensureLoaded(sequencePageQuery);
      this.searchResultObservables[blockIndex] = this.store.select(this.taskService.allByQuery(sequencePageQuery)).pipe(
        map(paginated => {
          return [...this.allSelectedTasks, ...paginated]
            .filter((seq, index, self) => index === self.findIndex(s => s.id === seq.id));
        })
      );
    }
  }

  async loadMoreTasks(event: { pageNumber: number, pageSize: number }, blockIndex: number) {
    if (!this.tasksSearchQuery) {
      const tasksPageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'name',
      };

      // Load next page from API
      await this.taskService.ensureLoaded(tasksPageQuery);

      // Get current sequences (previously loaded + selected)
      const currentTasks = await firstValueFrom(this.searchResultObservables[blockIndex] ?? of([]));

      // Combine previously loaded + new ones from store
      this.searchResultObservables[blockIndex] = this.store.select(this.taskService.allByQuery(tasksPageQuery)).pipe(
        map(paginated => {
          return [
            ...this.allSelectedTasks,
            ...currentTasks,
            ...paginated,
          ].filter((seq, index, self) => index === self.findIndex(s => s.id === seq.id));
        })
      );
    }
  }

  reorderTasks(event: CdkDragDrop<SequenceBlock[]>, blockIndex: number) {
    const control = this.formGroup.get('sequence');
    if (!control) {
      return;
    }

    const sequenceValue = control.value as SequenceList;
    const newList = [...sequenceValue.list];
    const targetBlock = [...(newList[blockIndex] || [])];
    moveItemInArray(targetBlock, event.previousIndex, event.currentIndex);
    newList[blockIndex] = targetBlock;

    control.setValue({ list: newList });
  }

  reorderBlocks(event: CdkDragDrop<SequenceBlock[][]>) {
    const control = this.formGroup.get('sequence');
    if (!control) {
      return;
    }

    const sequenceValue = control.value as SequenceList;
    const newList = [...sequenceValue.list];
    moveItemInArray(newList, event.previousIndex, event.currentIndex);

    control.setValue({ list: newList });
  }

  getTaskName(taskId: string) {
    return this.taskCache.get(taskId)?.name;
  }

  getTaskApproval(taskId: string): Approval {
    return this.taskCache.get(taskId)?.approval as Approval;
  }

  getBlockName(blockIndex: number): string {
    const sequenceValue = this.formGroup.get('sequence')?.value as SequenceList | undefined;
    const currentList = sequenceValue?.list || [];
    if (currentList[blockIndex]?.length <= 1) {
      return 'Aufgabe';
    }
    return 'zusammenhängende Aufgaben';
  }

  statusChanged(newStatus: string) {
    this.formGroup.controls.approval?.setValue(newStatus);
  }

  public getStateColor(taskId: string): string {
    return getStatusColor(this.getTaskApproval(taskId));
  }

  public getStateText(taskId: string): string {
    return getStatusText(this.getTaskApproval(taskId));
  }

  public addTaskBlock() {
    // add new values to both the form group and the map which handles the observables for the dropdown field
    this.searchResultObservables[Object.keys(this.searchResultObservables).length] = new Observable<TaskEntity[]>();
    const sequenceControl = this.formGroup.get('sequence');
    const sequenceValue = sequenceControl?.value as SequenceList | undefined;
    const currentList = sequenceValue?.list || [];
    const newList = [...currentList, []];
    sequenceControl?.patchValue({ list: newList });
    this.cdr.markForCheck();
  }

  public getlabelName(): string {
    if (this.isExamKind) {
      return 'Testaufgaben';
    }
    return 'Übungsaufgaben';
  }

  public getPlaceholder(): string {
    return 'Suchbegriff eingeben um Ergebnisse zu erhalten';
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
